import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Envelope, LockSimple, CircleNotch } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { BrandMark } from "@/components/Logo";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      navigate(res.user.role === "admin" ? "/admin" : from, { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="flex min-h-[80vh] items-center justify-center px-6 py-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md border border-cyan/20 bg-navy-surface p-8 sm:p-10"
        data-testid="login-page"
      >
        <div className="mb-8 flex flex-col items-center text-center">
          <BrandMark className="h-14 w-14" />
          <h1 className="mt-5 font-heading text-2xl font-black tracking-tight text-white">Welcome back</h1>
          <p className="mt-2 font-mono text-[11px] uppercase tracking-widest text-teal">
            Sign in to your research account
          </p>
        </div>

        {error && (
          <div data-testid="login-error" className="mb-5 border-l-2 border-destructive bg-destructive/10 px-4 py-2.5 font-mono text-xs text-destructive">
            {error}
          </div>
        )}

        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-teal">Email</label>
            <div className="relative">
              <Envelope size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-teal" />
              <input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@lab.com"
                className="w-full border border-cyan/15 bg-navy py-3 pl-9 pr-3 font-mono text-sm text-white placeholder:text-teal/40 outline-none focus:border-cyan/50"
              />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-teal">Password</label>
            <div className="relative">
              <LockSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-teal" />
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full border border-cyan/15 bg-navy py-3 pl-9 pr-3 font-mono text-sm text-white placeholder:text-teal/40 outline-none focus:border-cyan/50"
              />
            </div>
          </div>
          <button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 bg-cyan py-3.5 font-mono text-sm font-semibold uppercase tracking-wider text-navy-deep transition-all hover:bg-cyan-bright hover:shadow-glow disabled:opacity-60"
          >
            {loading ? <CircleNotch size={18} className="animate-spin" /> : "Sign In"}
          </button>
        </form>

        <p className="mt-6 text-center font-mono text-xs text-teal-muted">
          No account?{" "}
          <Link to="/register" data-testid="go-to-register" className="text-cyan hover:text-cyan-bright">
            Create one
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
